import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { commonAllergens } from '../../../../mocks/data/allergensData';
import AllergenChips from '../../../../components/shared/AllergenChips';
import { ProductAllergenEntry, ProductAllergenStatus } from '../types';

interface AllergensTabProps {
  allergens: ProductAllergenEntry[];
  onChange: (allergens: ProductAllergenEntry[]) => void;
  disabled?: boolean;
}

const AllergensTab: FC<AllergensTabProps> = ({ allergens, onChange, disabled }) => {
  const { t } = useTranslation('productManagement');

  const getStatus = (allergenId: number): ProductAllergenStatus => (
    allergens.find((entry) => entry.allergenId === allergenId)?.status ?? ProductAllergenStatus.FreeFrom
  );

  const handleStatusChange = (allergenId: number, status: ProductAllergenStatus) => {
    const next = allergens.filter((entry) => entry.allergenId !== allergenId);
    if (status !== ProductAllergenStatus.FreeFrom) {
      next.push({ allergenId, status });
    }
    onChange(next);
  };

  const activeAllergens = commonAllergens.filter((allergen) => getStatus(allergen.id) !== ProductAllergenStatus.FreeFrom);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="textLG"
        sx={{ mb: 4, fontWeight: 600 }}>
        {t('allergens', 'Allergens')}
      </Typography>

      {activeAllergens.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <AllergenChips
            allergens={activeAllergens.map((allergen) => ({
              allergenCode: allergen.code,
              allergenName: allergen.name,
              status: getStatus(allergen.id),
            }))}
            showName
            size="medium"
          />
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('allergenName', 'Allergen')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commonAllergens.map((allergen) => (
              <TableRow key={allergen.id}>
                <TableCell>{allergen.name}</TableCell>
                <TableCell>
                  <Select
                    value={getStatus(allergen.id)}
                    onChange={(e) => handleStatusChange(allergen.id, e.target.value as ProductAllergenStatus)}
                    size="small"
                    disabled={disabled}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value={ProductAllergenStatus.FreeFrom}>{t('allergenStatusFreeFrom', 'Free From')}</MenuItem>
                    <MenuItem value={ProductAllergenStatus.MayContain}>{t('allergenStatusMayContain', 'May Contain')}</MenuItem>
                    <MenuItem value={ProductAllergenStatus.Contains}>{t('allergenStatusContains', 'Contains')}</MenuItem>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AllergensTab;
